var m = require('mithril');
var prop = require('mithril/stream');
var moment = require('moment');
var dragscroll = require('../../libs/dragscroll');
var alarmsTablePagination = require('../../alarms/alarmsTablePagination');
var massPointOverrideModal = require('../../data/components/massPointOverrideModal');
var io = require('../../libs/socketio');

var overridesComponent = {};
var OVERRIDES_CHANGED_EVENT = 'overrides-changed';
var OVERRIDES_CHANGED_DEBOUNCE_MS = 750;

overridesComponent.oninit = function (vnode) {
  var ctrl = this;
  this.isExpanded = prop(false);
  this.selectedRecordsByPointRid = {};
  this.selectionLocationRid = null;
  this.overridesChangedTimer = null;
  this.onOverridesChanged = vnode && vnode.attrs && vnode.attrs.onOverridesChanged;
  this.handleOverridesChanged = function () {
    if (ctrl.overridesChangedTimer) clearTimeout(ctrl.overridesChangedTimer);
    ctrl.overridesChangedTimer = setTimeout(function () {
      ctrl.overridesChangedTimer = null;
      if (typeof ctrl.onOverridesChanged !== 'function') return;
      Promise.resolve().then(function () {
        return ctrl.onOverridesChanged();
      }).catch(function (error) {
        console.error('Failed to refresh Overrides after a socket notification:', error);
      });
    }, OVERRIDES_CHANGED_DEBOUNCE_MS);
  };
  io.on(OVERRIDES_CHANGED_EVENT, this.handleOverridesChanged);
};

overridesComponent.onupdate = function (vnode) {
  this.onOverridesChanged = vnode && vnode.attrs && vnode.attrs.onOverridesChanged;
};

overridesComponent.onremove = function () {
  if (this.overridesChangedTimer) {
    clearTimeout(this.overridesChangedTimer);
    this.overridesChangedTimer = null;
  }
  if (this.handleOverridesChanged) {
    io.off(OVERRIDES_CHANGED_EVENT, this.handleOverridesChanged);
  }
};

function normalizeRidValue(rid) {
  if (!rid) return null;
  var ridStr = rid.toString();
  return ridStr.charAt(0) === '#' ? ridStr : ('#' + ridStr);
}

function pointRidForRecord(record) {
  return normalizeRidValue(record && record.object && record.object['@rid']);
}

function actualRecords(records) {
  return (Array.isArray(records) ? records : []).filter(function (record) {
    return !!(record && record['@rid'] && pointRidForRecord(record));
  });
}

function isRecordSelected(ctrl, record) {
  var pointRid = pointRidForRecord(record);
  return !!(pointRid && ctrl.selectedRecordsByPointRid[pointRid]);
}

function selectedRecords(ctrl) {
  return Object.keys(ctrl.selectedRecordsByPointRid || {}).map(function (pointRid) {
    return ctrl.selectedRecordsByPointRid[pointRid];
  }).filter(Boolean);
}

function setRecordSelected(ctrl, record, selected) {
  var pointRid = pointRidForRecord(record);
  if (!pointRid) return;
  if (selected) {
    ctrl.selectedRecordsByPointRid[pointRid] = record;
  } else {
    delete ctrl.selectedRecordsByPointRid[pointRid];
  }
}

function setVisibleRecordsSelected(ctrl, records, selected) {
  actualRecords(records).forEach(function (record) {
    setRecordSelected(ctrl, record, selected);
  });
}

function normalizePathNode(node) {
  if (!node || typeof node !== 'object') return null;

  var rid = normalizeRidValue(node['@rid']);
  if (!rid) return null;

  var className = node['@class'];
  var displayName = node.name || node.example_name || className;

  if (displayName === 'root') {
    displayName = 'Home';
  } else if (className === 'Room' && node.roomNumber) {
    displayName = node.name ? `${node.name} ${node.roomNumber}` : node.roomNumber;
  } else if (typeof displayName === 'string' && displayName.charAt(0) === '-') {
    return null;
  }

  var href;
  if (displayName === 'Home') {
    href = '/';
  } else if (['Site', 'Building', 'Level', 'Room'].includes(className)) {
    href = `/location/${rid.slice(1)}`;
  } else if (className === 'Dashboard') {
    href = `/dashboard/${rid.slice(1)}`;
  } else if (node.url) {
    var normalizedUrl = node.url;
    if (normalizedUrl.startsWith('/#!/')) {
      normalizedUrl = '/' + normalizedUrl.slice(4);
    } else if (normalizedUrl.startsWith('#!/')) {
      normalizedUrl = '/' + normalizedUrl.slice(3);
    }
    href = normalizedUrl;
  } else if (className && className !== 'Dataset' && className !== 'Element' && className !== 'Column') {
    href = `/equipment/${className.toLowerCase()}/${rid.slice(1)}`;
  }

  return {
    rid: rid,
    // displayName: `${displayName} (${className})`,
    displayName: displayName || className,
    href: href,
    className: className
  };
}

function renderNodeLink(node) {
  if (['Site', 'Building', 'Level', 'Room'].includes(node.className)) {
    return m(m.route.Link, {
      href: `/location/${node.rid.slice(1)}`
    }, node.displayName);
  }

  if (node.className === 'Dashboard') {
    return m(m.route.Link, { href: `/dashboard/${node.rid.slice(1)}` }, node.displayName);
  }

  if (node.href) {
    if (node.href.startsWith('/')) {
      return m(m.route.Link, { href: node.href }, node.displayName);
    }
    return m('a', { href: node.href, target: '_blank', rel: 'noopener noreferrer' }, node.displayName);
  }

  return node.displayName;
}

function selectPath(record, locationRid) {
  if (!record || !record.object || !Array.isArray(record.object.usedOn)) return null;

  var targetRid = normalizeRidValue(locationRid);
  var fallback = null;

  for (var i = 0; i < record.object.usedOn.length; i++) {
    var path = record.object.usedOn[i];
    if (!Array.isArray(path) || !path.length) continue;
    var hasElement = path.some(function (node) {
      return node && (node['@class'] === 'Element' || node.className === 'Element');
    });
    if (hasElement) continue;
    if (!fallback) fallback = path;
    if (!targetRid) continue;
    var includesLocation = path.some(function (node) {
      return normalizeRidValue(node && node['@rid']) === targetRid;
    });
    if (includesLocation) {
      return path;
    }
  }

  return fallback;
}

function buildPathContent(record, locationRid) {
  var pathNodes = selectPath(record, locationRid) || [];
  var normalized = pathNodes.map(normalizePathNode).filter(Boolean);

  var pointRid = record.object && record.object['@rid'] ? normalizeRidValue(record.object['@rid']) : null;
  var pointName = record.object && (record.object.name || record.object['@class']);

  if (!normalized.length && !pointName) {
    return m('span', 'No path');
  }

  var segments = normalized.map(function (node, idx) {
    if (node.displayName === 'Home') {
      return ;
    }

    var separator = node.className === 'Column' ? '-' : '/';

    return m('div.nowrap.truncate.flex', [
      renderNodeLink(node),
      m('div.mh1', separator)
    ]);
  });

  if (pointName) {
    var pointLink = pointRid
      ? m('a', { href: `#/point/${pointRid.slice(1)}` }, pointName)
      : pointName;
    segments.push(m('div.mr2.nowrap.truncate', pointLink));
  }

  return m('div.flex', segments);
}

function buildPathLabel(record, locationRid) {
  var pathNodes = selectPath(record, locationRid) || [];
  var labels = pathNodes.map(normalizePathNode).filter(Boolean).map(function (node) {
    return node.displayName;
  }).filter(function (label) {
    return label && label !== 'Home';
  });
  var pointName = record && record.object &&
    (record.object.name || record.object['@class'] || record.object['@rid']);
  if (pointName) labels.push(pointName);
  return labels.join(' / ') || 'Point';
}

function releaseRows(ctrl, records, locationRecord, onReleaseComplete) {
  var uniqueRecordsByPointRid = {};
  actualRecords(records).forEach(function (record) {
    uniqueRecordsByPointRid[pointRidForRecord(record)] = record;
  });
  var recordsToRelease = Object.keys(uniqueRecordsByPointRid).map(function (pointRid) {
    return uniqueRecordsByPointRid[pointRid];
  });
  var parentRid = normalizeRidValue(locationRecord && locationRecord['@rid']);
  if (!recordsToRelease.length || !parentRid) return;

  var count = recordsToRelease.length;
  massPointOverrideModal.editDialogue({
    title: count === 1
      ? 'Release Override: ' + buildPathLabel(recordsToRelease[0], parentRid)
      : 'Release ' + count + ' Overrides',
    parentRid: parentRid,
    releaseOnly: true,
    rows: recordsToRelease.map(function (record) {
      return {
        path: buildPathLabel(record, parentRid),
        columnRecord: { in: record.object }
      };
    }),
    submitCallback: async function (summary) {
      recordsToRelease.forEach(function (record) {
        setRecordSelected(ctrl, record, false);
      });
      if (typeof onReleaseComplete === 'function') {
        await onReleaseComplete(summary);
      }
    }
  });
}

function formatUser(record) {
  if (!record) return '---';
  var props = record.properties || {};
  var hasPropsName = props.firstName || props.lastName;
  if (hasPropsName) {
    return `${props.firstName || ''} ${props.lastName || ''}`.trim() || '---';
  }

  var user = record.user || {};
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || '---';
  }

  return user.name || '---';
}

function formatTimestamp(record) {
  if (!record || !record.timestamp) return '---';
  var mValue = moment(record.timestamp);
  if (!mValue.isValid()) return '---';
  return mValue.format('MM-DD-YYYY hh:mm:ss a');
}

function formatRelinquishTime(record) {
  var schedule = record && record.relinquishSchedule;
  if (!schedule || !schedule.releaseAt) return '---';
  var releaseTime = moment(Number(schedule.releaseAt));
  if (!releaseTime.isValid()) return '---';

  var status = String(schedule.status || '').toLowerCase();
  var statusLabel = {
    retry: 'Retry pending',
    processing: 'Releasing',
    canceling: 'Canceling'
  }[status];
  return m('div.inline-flex.items-center.nowrap.overrides-relinquish-content', [
    m('span.nowrap', releaseTime.format('MM-DD-YYYY hh:mm:ss a')),
    statusLabel ? m('span.f6.moon-gray.nowrap.ml2', statusLabel) : null
  ]);
}

function formatNumericValue(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return '---';
  }

  var parsed = parseFloat(value);
  if (Number.isNaN(parsed)) {
    return String(value);
  }
  return parsed.toFixed(2);
}

function formatValue(record) {
  var props = record && record.properties;
  if (!props || typeof props.value === 'undefined' || props.value === null) return '---';

  var unit = props.unit || (record.object && record.object.unit);

  var formattedValue = formatNumericValue(props.value);
  if (formattedValue === '---') return formattedValue;
  if (unit && unit !== '---') {
    return m('span', [
      formattedValue,
      m('span.v-top.dib.moon-gray', {style: {fontSize: '10px'}}, unit)
    ]);
  }
  return formattedValue;
}

function formatStatus(record) {
  var props = record && record.properties;
  if (!props || typeof props.active === 'undefined' || props.active === null) return '---';
  return Number(props.active) === 1 ? 'Activated' : 'Released';
}

function selectionButton(options) {
  var checked = !!options.checked;
  var mixed = !!options.mixed;
  var disabled = !!options.disabled;
  return m('button', {
    type: 'button',
    class: 'ba bw1 br1 flex items-center justify-center center ' + (options.class || ''),
    style: {
      width: '1.35rem',
      height: '1.35rem',
      minWidth: '1.35rem',
      padding: 0,
      borderColor: checked || mixed ? '#58abe2' : '#777',
      backgroundColor: checked || mixed ? '#58abe2' : 'transparent',
      color: checked || mixed ? '#111' : '#f4f4f4',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      lineHeight: 1
    },
    disabled: disabled,
    title: options.label,
    'aria-label': options.label,
    'aria-checked': mixed ? 'mixed' : checked ? 'true' : 'false',
    role: 'checkbox',
    'data-select-all-children': options.selectAll ? true : null,
    'data-bulk-select-rid': options.rid || null,
    onmousedown: function (evt) {
      evt.stopPropagation();
    },
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') options.onclick();
    },
    onkeydown: function (evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') options.onclick();
    }
  }, [
    checked ? m('i.f4.ion-checkmark') : mixed ? m('span.b.f4', '-') : null
  ]);
}

function releaseButton(label, onclick, disabled, extraClass) {
  var className = 'f5 ba bw1 ph3 pv2 tc v-btm dib nowrap';
  if (extraClass) className += ' ' + extraClass;
  if (!disabled) className += ' dim pointer';

  return m('button', {
    type: 'button',
    class: className,
    disabled: !!disabled,
    'aria-disabled': disabled ? 'true' : 'false',
    style: {
      minWidth: '10.5rem',
      borderColor: disabled ? '#555' : '#777',
      backgroundColor: 'transparent',
      color: disabled ? '#888' : '#ddd',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease, opacity 0.12s ease'
    },
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof onclick === 'function') onclick();
    }
  }, label);
}

function selectedCellStyle(selected) {
  return selected ? { backgroundColor: '#1f4358' } : undefined;
}

function selectionCellStyle(selected) {
  return {
    width: '2.5rem',
    minWidth: '2.5rem',
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: selected ? '#1f4358' : undefined
  };
}

function relinquishTimeCellStyle(selected) {
  return {
    whiteSpace: 'nowrap',
    backgroundColor: selected ? '#1f4358' : undefined
  };
}

function createDataTd(content, extraClass, style) {
  var classes = 'bb bl-0 pr3 truncate' + (extraClass || '');
  return m('td', { class: classes, style: style }, content || m.trust('&nbsp;'));
}

function createRow(record, options) {
  if (!record || !record['@rid']) {
    return m('tr', Array.from({ length: 7 }).map(function () {
      return createDataTd();
    }));
  }

  var selected = isRecordSelected(options.ctrl, record);
  var cellStyle = selectedCellStyle(selected);
  var pointName = record.object && (record.object.name || record.object['@rid']) || 'point';
  return m('tr', { 'data-override-point-rid': pointRidForRecord(record) }, [
    m('td.bb.bl-0.tc.nowrap.v-mid.overrides-select-cell', {
      style: selectionCellStyle(selected)
    }, selectionButton({
      checked: selected,
      disabled: options.disabled,
      label: (selected ? 'Deselect ' : 'Select ') + pointName,
      rid: pointRidForRecord(record),
      class: 'overrides-row-checkbox',
      onclick: function () {
        setRecordSelected(options.ctrl, record, !isRecordSelected(options.ctrl, record));
      }
    })),
    createDataTd(buildPathContent(record, options.locationRid), ' overflow-hidden', cellStyle),
    createDataTd(formatUser(record), ' nowrap', cellStyle),
    createDataTd(formatTimestamp(record), ' nowrap', cellStyle),
    createDataTd(formatValue(record), '', cellStyle),
    createDataTd(formatStatus(record), '', cellStyle),
    createDataTd(
      formatRelinquishTime(record),
      ' nowrap overrides-relinquish-time-cell',
      relinquishTimeCellStyle(selected)
    )
  ]);
}

function toTimestamp(value) {
  if (value === null || typeof value === 'undefined') return -Infinity;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    var numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric;
    var parsedDate = Date.parse(value);
    return Number.isNaN(parsedDate) ? -Infinity : parsedDate;
  }
  var parsed = Date.parse(value);
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

function renderTable(records, options) {
  var list = Array.isArray(records) ? records : [];
  var actualRecords = list.filter(function (record) { return record && record['@rid']; });
  var fillerRecords = list.filter(function (record) { return !record || !record['@rid']; });

  actualRecords.sort(function (a, b) {
    var tsA = toTimestamp(a && a.timestamp);
    var tsB = toTimestamp(b && b.timestamp);
    if (options.sortAscending) {
      if (tsA === -Infinity && tsB !== -Infinity) return 1;
      if (tsB === -Infinity && tsA !== -Infinity) return -1;
      return tsA - tsB;
    }
    if (tsA === -Infinity && tsB !== -Infinity) return 1;
    if (tsB === -Infinity && tsA !== -Infinity) return -1;
    return tsB - tsA;
  });

  var ordered = actualRecords.concat(fillerRecords);
  var visibleRecords = actualRecords.filter(function (record) {
    return !!pointRidForRecord(record);
  });
  var selectedVisibleCount = visibleRecords.filter(function (record) {
    return isRecordSelected(options.ctrl, record);
  }).length;
  var allVisibleSelected = visibleRecords.length > 0 && selectedVisibleCount === visibleRecords.length;
  var someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  return m('div.overflow-hidden.mb3', [
    m('div.dragscroll-container', {
      style: { 'margin-right': '0px'}
    }, [
      m('div.dragscroll.pb3', { oncreate: dragscroll }, [
        m('table.f4.pb3.narrow.w-100', [
          m('tbody', [
            m('tr', [
              m('td.bb.moon-gray.tc.nowrap.v-mid.overrides-select-cell', {
                style: selectionCellStyle(false)
              }, [
                selectionButton({
                  checked: allVisibleSelected,
                  mixed: someVisibleSelected,
                  disabled: options.disabled || visibleRecords.length === 0,
                  label: allVisibleSelected ? 'Deselect visible overrides' : 'Select visible overrides',
                  class: 'overrides-select-all-checkbox',
                  selectAll: true,
                  onclick: function () {
                    setVisibleRecordsSelected(options.ctrl, visibleRecords, !allVisibleSelected);
                  }
                })
              ]),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Path'),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'User'),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden' + (typeof options.onToggleSort === 'function' ? '.pointer' : ''),
                typeof options.onToggleSort === 'function' ? { onclick: options.onToggleSort } : {}, [
                  'Timestamp',
                  options.sortAscending
                    ? m('i.ion-ios-arrow-up.ml2.blue')
                    : m('i.ion-ios-arrow-down.ml2.blue')
                ]
              ),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Value'),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', 'Status'),
              m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden.overrides-relinquish-time-cell', {
                style: relinquishTimeCellStyle(false)
              }, 'Relinquish Time')
            ]),
            ordered.map(function (record) {
              return createRow(record, options);
            })
          ])
        ])
      ])
    ])
  ]);
}

overridesComponent.view = function (vnode) {
  var ctrl = this;
  var ridsProp = vnode.attrs.rids;
  var recordsProp = vnode.attrs.records;
  var isLoadingProp = vnode.attrs.isLoading;
  var locationProp = vnode.attrs.location;
  var pageSize = vnode.attrs.pageSize || 10;

  var ridsValue = typeof ridsProp === 'function' ? ridsProp() : ridsProp;
  var recordsValue = typeof recordsProp === 'function' ? recordsProp() : recordsProp;
  var rids = Array.isArray(ridsValue) ? ridsValue : [];
  var records = Array.isArray(recordsValue) ? recordsValue : [];
  var isLoading = typeof isLoadingProp === 'function' ? isLoadingProp() : !!isLoadingProp;
  var locationRecord = typeof locationProp === 'function' ? locationProp() : locationProp;
  var locationRid = locationRecord && locationRecord['@rid'];
  var normalizedLocationRid = normalizeRidValue(locationRid);
  if (ctrl.selectionLocationRid !== normalizedLocationRid) {
    ctrl.selectionLocationRid = normalizedLocationRid;
    ctrl.selectedRecordsByPointRid = {};
  }
  var locationForPagination = locationRecord && locationRecord['@rid'] ? locationRecord : { '@rid': '' };
  var sortAscendingAttr = vnode.attrs.sortAscending;
  var sortAscending = typeof sortAscendingAttr === 'function' ? !!sortAscendingAttr() : !!sortAscendingAttr;
  var onToggleSort = vnode.attrs.onToggleSort;
  var onReleaseComplete = vnode.attrs.onReleaseComplete;
  var sortToken = typeof vnode.attrs.sortToken === 'undefined'
    ? (sortAscending ? 'asc' : 'desc')
    : vnode.attrs.sortToken;
  var refreshKey = String(sortToken) + ':' + String(vnode.attrs.refreshToken || 0);

  var total = rids.length;
  var selected = selectedRecords(ctrl);
  var expanded = ctrl.isExpanded();
  var canExpand = total > 0;

  if (!canExpand && expanded) {
    ctrl.isExpanded(false);
    expanded = false;
  }

  var containerClass = `div.cf.mh3.bw1.mv2.b--grey${expanded ? '.pv3.bt.pl2.bl' : '.pa3.ba'}`;

  return m(containerClass, {
    style: { 
      'border-left-style': expanded ? 'dotted' : 'solid',    
    }
  },[
    m(`div.v-btm.flex.justify-between.items-center.flex-wrap.f2`, {
      onclick: canExpand && !isLoading ? function () {
        ctrl.isExpanded(!ctrl.isExpanded());
      } : undefined
    }, [
      m('div.flex.items-center.overflow-hidden', [
        m('div.yellow', 'Overrides'),
      ]),
      m('div.flex.items-center.flex-wrap', [
        m('label.mr2.f5.moon-gray', 'Active:'),        
        m('div.br2.nowrap.mr2', { style: { 'background-color': '#262626' } }, [
          m('label.f4.ph1.yellow', `${total}`)
        ]),
        canExpand
        ? m(`i.icon.fr.ion-chevron-down.blue${expanded ? '.ion-close.f3.mr3' : '.ion-chevron-down'}`)
        : null
      ])
    ]),
    canExpand
      ? m(`div${expanded ? '' : '.dn'}`, [
          m('div.flex.justify-end.items-center.mt3.mb2', [
            releaseButton(
              selected.length ? 'Release Selected (' + selected.length + ')' : 'Release Selected',
              function () {
                releaseRows(ctrl, selected, locationRecord, onReleaseComplete);
              },
              isLoading || selected.length === 0,
              'overrides-release-selected'
            )
          ]),
          m('div.mt2', renderTable(records, {
            ctrl: ctrl,
            locationRid: locationRid,
            locationRecord: locationRecord,
            sortAscending: sortAscending,
            onToggleSort: onToggleSort,
            onReleaseComplete: onReleaseComplete,
            disabled: isLoading
          })),
          m('div.mt3', [
            m(alarmsTablePagination, {
              data: [...rids],
              url: '/api/v1/overrides/paginated',
              recordsToShow: recordsProp,
              location: locationForPagination,
              numberOfColumns: pageSize,
              refreshKey: refreshKey
            })
          ])
        ])
      : ''
  ]);
};

module.exports = overridesComponent;
